import { PriceTag } from "@/components/PriceTag";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useT } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/context/CatalogContext";
import { TESTIDS } from "@/constants/testIds";
import StockBadge from "@/components/StockBadge";
import ProgressiveImage from "@/components/ProgressiveImage";
import { cldImage, cldSrcSet, imgPos } from "@/lib/utils";

// ─── Product card (compact, quick-add) ────────────────────────────
const ProductCard = ({ p, priority = false }) => {
  const { t } = useT();
  const cart = useCart();
  const [flash, setFlash] = useState(false);

  // Find the first variant with stock > 0 so quick-add doesn't silently
  // add a sold-out variant to the cart.
  const firstAvailable = (p.variants || []).find((v) => (v.stock ?? 0) > 0);
  const soldOut = !firstAvailable;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    cart.addItem({
      productId: p.id,
      slug: p.slug,
      name: p.name,
      image: p.images?.[0],
      price: p.price,
      originalPrice: p.original_price,
      variantName: firstAvailable.name || "",
      quantity: 1,
      stock: firstAvailable.stock,
    });
    setFlash(true);
    setTimeout(() => setFlash(false), 1200);
  };

  const front = p.images?.[0];
  const secondary = p.images?.[1];
  // Only enable the hover swap when there IS a distinct second image;
  // otherwise the front would fade out and reveal the empty card bg
  // (the "blank on hover" bug on single-image products).
  const hasBack = Boolean(secondary && secondary !== front);

  return (
    <Link
      to={`/product/${p.slug}`}
      data-testid={TESTIDS.productCardLink(p.slug)}
      data-scroll-anchor={p.slug}
      className="group block gl-card-vis"
    >
      <div className="relative rounded-card aspect-[3/4] overflow-hidden bg-[var(--gl-bg-alt)]">
        <div className="absolute top-2.5 left-2.5 z-10 flex flex-col items-start gap-1.5">
          {p.trending && (
            <div
              data-testid={TESTIDS.productCardTrending(p.slug)}
              className="bg-[var(--gl-orange)] text-[var(--gl-orange-text)] font-badge text-[9px] px-2.5 py-1 rounded-full"
            >TRENDING</div>
          )}
          {p.back_in_stock && (
            <div
              data-testid={`product-card-back-in-stock-${p.slug}`}
              className="bg-[#241C18] text-[var(--gl-orange)] font-badge text-[9px] px-2.5 py-1 rounded-full"
            >{t("badge.backInStock")}</div>
          )}
        </div>
        <div className="absolute top-2.5 right-2.5 z-10">
          <StockBadge product={p} />
        </div>
        <ProgressiveImage
          src={cldImage(front, 800)}
          srcSet={cldSrcSet(front, [400, 600, 800, 1200])}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={p.name}
          priority={priority}
          position={imgPos(p, front)}
          className={`gl-card-swap-front absolute inset-0 w-full h-full object-cover ${
            hasBack ? "md:group-hover:opacity-0" : ""
          }`}
        />
        {hasBack && (
          <img
            src={cldImage(secondary, 800)}
            srcSet={cldSrcSet(secondary, [400, 600, 800, 1200])}
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            alt=""
            loading="lazy"
            decoding="async"
            style={{ objectPosition: imgPos(p, secondary) }}
            className="gl-card-swap-back absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500 md:group-hover:opacity-100"
          />
        )}
        <button
          data-testid={`collection-quickadd-${p.slug}`}
          onClick={quickAdd}
          aria-label={soldOut ? `${p.name} — sold out` : `Add ${p.name} to bag`}
          disabled={soldOut}
          className={`absolute bottom-2.5 right-2.5 z-10 h-9 w-9 rounded-full flex items-center justify-center shadow-md transition-all ${
            soldOut
              ? "bg-white/70 text-[#8A7568] cursor-not-allowed"
              : flash
              ? "bg-[#241C18] text-[var(--gl-orange-text)] scale-110"
              : "bg-white/95 text-[#241C18] hover:bg-[var(--gl-orange)] hover:text-[var(--gl-orange-text)] hover:scale-105"
          }`}
        >
          {soldOut ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4l16 16M4 20L20 4"/></svg>
          ) : flash ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12l5 5L20 7"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
          )}
        </button>
      </div>
      <div className="mt-2.5 px-0.5">
        <div className="font-serif-display text-sm md:text-base leading-snug text-[#241C18] min-h-[2.6em]">
          {p.name}
        </div>
        <div className="mt-0.5 font-serif-display text-sm md:text-base font-medium text-[#241C18]">
          <PriceTag product={p} testid={`product-card-price-${p.slug}`} />
        </div>
      </div>
    </Link>
  );
};

const Collection = () => {
  const { t } = useT();
  const params = useParams();
  const category = params.category || "all";
  const { products: allProducts, loading, categories } = useCatalog();
  const [sortBy, setSortBy] = useState("featured");

  const SORTS = useMemo(() => ([
    { key: "featured", label: t("sort.featured") },
    { key: "newest", label: t("sort.newest") },
    { key: "price-asc", label: t("sort.priceAsc") },
    { key: "price-desc", label: t("sort.priceDesc") },
    { key: "name-asc", label: t("sort.nameAsc") },
  ]), [t]);

  // Filter cached catalog client-side. Keeps Back-navigation instant (no
  // network roundtrip) so the grid height stays stable during scroll
  // restoration — otherwise the browser lands past the end of an empty
  // loading state.
  const products = useMemo(() => {
    if (!Array.isArray(allProducts)) return [];
    let list;
    if (category === "trending" || category === "sale") {
      list = allProducts.filter((p) => p.trending);
    } else if (category === "skirts-trousers") {
      list = allProducts.filter((p) => p.category === "skirts" || p.category === "trousers");
    } else if (category === "new-in") {
      // Novidades page — only pieces the admin has flagged with the
      // `novidade` checkbox. Preserves the backend-supplied order
      // (admin position asc, then newest first for un-ordered rows).
      list = allProducts.filter((p) => p.novidade);
    } else if (category === "all") {
      list = allProducts;
    } else {
      list = allProducts.filter((p) => p.category === category);
    }
    // Apply shopper-selected sort on top of the filter. "featured" keeps
    // the server-supplied order (admin position + newest fallback), so it
    // acts as a no-op passthrough.
    if (sortBy === "featured") return list;
    const copy = [...list];
    if (sortBy === "newest") {
      copy.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === "price-asc") {
      copy.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === "price-desc") {
      copy.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortBy === "name-asc") {
      copy.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    }
    return copy;
  }, [allProducts, category, sortBy]);

  const title = useMemo(() => {
    if (category === "all") return t("nav.collection");
    if (category === "new-in") return t("nav.novidades");
    if (category === "trending" || category === "sale") return t("nav.trending");
    if (category === "skirts-trousers") return t("nav.skirtsTrousers");
    const c = categories.find((x) => x.slug === category);
    return c ? c.label : t("nav.collection");
  }, [category, categories, t]);

  return (
    <div className="pt-24 md:pt-32 pb-24">
      <header className="px-6 md:px-12 mb-8 md:mb-10 text-center">
        <h1
          data-testid="collection-title"
          className="font-serif-display text-4xl md:text-6xl tracking-tight text-[#241C18]"
        >
          {title}
        </h1>
      </header>

      {/* Sort control only — category filters now live in the top nav */}
      <div className="px-6 md:px-12 mb-8 md:mb-10 flex justify-end">
        <label className="flex items-center gap-2 whitespace-nowrap">
          <span className="font-typewriter text-[10px] tracking-[0.22em] uppercase text-[var(--gl-muted)] hidden md:inline">
            {t("sort.label")}
          </span>
          <select
            data-testid="collection-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[var(--gl-bg-alt)] border border-[var(--gl-bg-alt)] hover:border-[#241C18] focus:border-[var(--gl-orange)] outline-none rounded-full px-4 py-2 text-xs md:text-sm font-badge tracking-[0.05em] uppercase transition cursor-pointer"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="px-6 md:px-12 font-typewriter text-xs tracking-[0.22em] text-[var(--gl-muted)]">
          {t("collection.developing")}
        </div>
      ) : products.length === 0 ? (
        <div className="px-6 md:px-12 py-10 text-center text-[var(--gl-muted)]">
          <div className="font-serif-display text-2xl">Nothing here yet.</div>
          <div className="mt-2 text-sm">Check back soon — new pieces drop weekly.</div>
        </div>
      ) : (
        <div
          data-testid={TESTIDS.collectionGrid}
          className="px-4 md:px-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-8 md:gap-x-5 md:gap-y-10"
        >
          {products.map((p, i) => (
            // First 4 tiles are above the fold on mobile (2×2) and desktop
            // (up to 4 cols) — flag them as `priority` so the browser eagerly
            // fetches them with high priority instead of waiting on the
            // lazy-loading intersection observer.
            <ProductCard key={p.id} p={p} priority={i < 4} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Collection;
